import { useState, useEffect, useCallback } from 'react';
import PageHeader from '@/components/Layout/PageHeader';
import { accessAPI, employeeAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { MagnifyingGlass, IdentificationBadge, Buildings, UserGear } from '@phosphor-icons/react';

export default function TeamAccessPage() {
  const [employees, setEmployees] = useState([]);
  const [roles, setRoles] = useState([]);
  const [clinics, setClinics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ role_id: '', allocated_clinic_ids: [], portal_access: true });
  const [clinicSearch, setClinicSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [settingPw, setSettingPw] = useState(false);

  const load = useCallback(async (q = '') => {
    setLoading(true);
    try {
      const { data } = await accessAPI.employees({ search: q });
      setEmployees(data.employees || []);
      setRoles(data.roles || []);
    } catch (e) {
      toast.error('Failed to load team');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    accessAPI.clinics().then(({ data }) => setClinics(data.clinics || [])).catch(() => {});
  }, [load]);

  useEffect(() => {
    const t = setTimeout(() => load(search), 350);
    return () => clearTimeout(t);
  }, [search, load]);

  const roleName = (id) => roles.find(r => r.id === id)?.name || '—';
  const clinicName = (id) => clinics.find(c => c.id === id)?.name || id;

  const openManage = (e) => {
    setEditing(e);
    setForm({
      role_id: e.role_id || '',
      allocated_clinic_ids: e.allocated_clinic_ids || [],
      portal_access: e.portal_access !== false,
    });
    setClinicSearch('');
    setNewPassword('');
  };

  const handleSetPassword = async () => {
    if (!newPassword.trim() || newPassword.length < 4) { toast.error('Enter a password (min 4 chars)'); return; }
    setSettingPw(true);
    try {
      await employeeAPI.setPassword({ employee_number: editing.employee_number, password: newPassword });
      toast.success(`Password set for ${editing.name}`);
      setNewPassword('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to set password');
    } finally { setSettingPw(false); }
  };

  const toggleClinic = (id) => setForm(f => ({
    ...f,
    allocated_clinic_ids: f.allocated_clinic_ids.includes(id)
      ? f.allocated_clinic_ids.filter(x => x !== id)
      : [...f.allocated_clinic_ids, id],
  }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await accessAPI.updateEmployee(editing.employee_number, {
        role_id: form.role_id || null,
        allocated_clinic_ids: form.allocated_clinic_ids,
        portal_access: form.portal_access,
      });
      toast.success(`Access updated for ${editing.name}`);
      setEditing(null);
      load(search);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const filteredClinics = clinics.filter(c =>
    !clinicSearch || c.name.toLowerCase().includes(clinicSearch.toLowerCase()) || (c.code || '').toLowerCase().includes(clinicSearch.toLowerCase())
  );

  return (
    <div data-testid="team-access-page">
      <PageHeader
        title="Team Access"
        subtitle="Assign a role and clinic access to each employee. They log in to the clinic & HR portals with their work email."
      />

      <div className="page-content space-y-4">
        <div className="relative max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input placeholder="Search by name, email, employee no. or department..." value={search}
            onChange={e => setSearch(e.target.value)} className="pl-9" data-testid="team-search" />
        </div>

        <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
          <table className="w-full text-sm" data-testid="team-table">
            <thead>
              <tr className="bg-[#F8FAFC] text-[#64748B] text-xs">
                <th className="text-left p-3">Employee</th>
                <th className="text-left p-3">Department</th>
                <th className="text-left p-3">Role</th>
                <th className="text-left p-3">Clinic Access</th>
                <th className="text-center p-3">Portal</th>
                <th className="text-center p-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="text-center py-8">
                  <div className="inline-block w-5 h-5 border-2 border-[#0F4C81] border-t-transparent rounded-full animate-spin" />
                </td></tr>
              ) : employees.length ? employees.map(e => (
                <tr key={e.employee_number} className="border-t border-[#E2E8F0] hover:bg-[#F8FAFC]" data-testid={`team-row-${e.employee_number}`}>
                  <td className="p-3">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-[#E2E8F0] flex items-center justify-center">
                        <IdentificationBadge size={16} className="text-[#64748B]" />
                      </div>
                      <div>
                        <div className="font-medium text-[#0F172A]">{e.name}</div>
                        <div className="text-xs text-[#64748B]">{e.email || e.employee_number}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 text-[#64748B]">{e.department || '—'}</td>
                  <td className="p-3">
                    {e.role_id
                      ? <span className="text-xs px-2 py-1 rounded-full bg-[#EBF3FA] text-[#0F4C81]">{e.role_name || roleName(e.role_id)}</span>
                      : <span className="text-xs text-gray-400">No role</span>}
                  </td>
                  <td className="p-3 text-[#64748B] text-xs">
                    {e.allocated_clinic_ids?.length
                      ? <span title={e.allocated_clinic_ids.map(clinicName).join(', ')}>{e.allocated_clinic_ids.length} clinic{e.allocated_clinic_ids.length > 1 ? 's' : ''}</span>
                      : (roles.find(r => r.id === e.role_id)?.all_clinics ? 'All clinics' : '—')}
                  </td>
                  <td className="p-3 text-center">
                    {e.portal_access !== false
                      ? <span className="text-xs text-emerald-700">Enabled</span>
                      : <span className="text-xs text-gray-400">Disabled</span>}
                  </td>
                  <td className="p-3 text-center">
                    <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => openManage(e)} data-testid={`manage-access-${e.employee_number}`}>
                      <UserGear size={14} className="mr-1" /> Manage
                    </Button>
                  </td>
                </tr>
              )) : (
                <tr><td colSpan={6} className="text-center py-8 text-[#64748B]">No employees found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="manage-access-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Plus_Jakarta_Sans']">Manage Access — {editing?.name}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-[#64748B] -mt-2">{editing?.email} · {editing?.job_title || editing?.department}</p>

          <div className="mt-2">
            <label className="form-label">Role</label>
            <Select value={form.role_id || 'none'} onValueChange={v => setForm(f => ({ ...f, role_id: v === 'none' ? '' : v }))}>
              <SelectTrigger data-testid="access-role-select"><SelectValue placeholder="Select a role" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No role (no portal access)</SelectItem>
                {roles.map(r => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          <div className="mt-3">
            <div className="flex items-center justify-between mb-1">
              <label className="form-label mb-0">Clinic Access</label>
              <span className="text-xs text-[#0F4C81]">{form.allocated_clinic_ids.length} selected</span>
            </div>
            {roles.find(r => r.id === form.role_id)?.all_clinics ? (
              <p className="text-xs text-[#64748B] bg-[#EBF3FA] rounded-md p-2 flex items-center gap-1">
                <Buildings size={13} /> This role already grants access to <strong>all clinics</strong>.
              </p>
            ) : (
              <div className="border border-[#E2E8F0] rounded-lg">
                <div className="p-2 border-b border-[#E2E8F0]">
                  <Input value={clinicSearch} onChange={e => setClinicSearch(e.target.value)} placeholder="Search clinics..." className="h-8 text-sm" data-testid="access-clinic-search" />
                </div>
                <div className="max-h-56 overflow-y-auto p-2 space-y-1">
                  {filteredClinics.map(c => (
                    <label key={c.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-[#EBF3FA] cursor-pointer" data-testid={`access-clinic-${c.id}`}>
                      <input type="checkbox" checked={form.allocated_clinic_ids.includes(c.id)} onChange={() => toggleClinic(c.id)}
                        className="rounded border-gray-300" />
                      <span className="text-sm text-[#0F172A]">{c.name} {c.code ? <span className="text-xs text-gray-400">({c.code})</span> : ''}</span>
                    </label>
                  ))}
                  {!filteredClinics.length && <p className="text-xs text-gray-400 text-center py-3">No clinics</p>}
                </div>
              </div>
            )}
          </div>

          <label className="flex items-center gap-2 cursor-pointer mt-3">
            <input type="checkbox" checked={form.portal_access} onChange={e => setForm(f => ({ ...f, portal_access: e.target.checked }))}
              className="rounded border-gray-300" data-testid="access-portal-toggle" />
            <span className="text-sm text-[#0F172A]">Allow portal login (clinic & HR) with work email</span>
          </label>

          <div className="mt-4 border-t border-[#E2E8F0] pt-3">
            <label className="form-label">Portal Password</label>
            <p className="text-xs text-[#64748B] mb-1.5">Set or reset the password this employee uses to log in with their email (default is <code>hearclear@123</code>).</p>
            <div className="flex gap-2">
              <Input value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="New password"
                className="h-9" data-testid="access-new-password" />
              <Button variant="outline" onClick={handleSetPassword} disabled={settingPw || !newPassword.trim()} data-testid="set-password-btn">
                {settingPw ? 'Setting...' : 'Set Password'}
              </Button>
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button className="bg-[#0F4C81] hover:bg-[#0B3C68]" onClick={handleSave} disabled={saving} data-testid="save-access-btn">
              {saving ? 'Saving...' : 'Save Access'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
